import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { formatPrice } from "../lib/api";
import { ProductVisual } from "./ProductVisual";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const OptionRow = ({ label, options, value, onChange, minWidth = "" }) => (
  <div className="mb-4">
    <p className="text-[10px] uppercase tracking-[0.25em] text-capra-muted mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`${minWidth} px-3 py-1.5 text-[11px] border transition-colors ${
            value === o ? "border-capra-bronze text-capra-bronze" : "border-white/15 text-capra-muted hover:border-white/40"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const QuickViewDetails = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(product.colors?.[0] || null);

  const add = () => {
    if (product.sizes?.length > 0 && !size) {
      toast.error("Please select a size.");
      return;
    }
    addItem(product, { size, color });
    onClose();
    toast.success(`${product.name} added to your cart.`);
  };

  return (
    <div className="p-8 flex flex-col">
      <p className="overline-label mb-2">{product.category.replace("-", " ")}</p>
      <DialogTitle asChild>
        <h2 className="font-display text-2xl text-capra-ivory mb-2">{product.name}</h2>
      </DialogTitle>
      <p className="text-capra-gold mb-4">{formatPrice(product.price)}</p>
      <p className="text-sm text-capra-muted leading-relaxed mb-6 line-clamp-3">{product.description}</p>

      {product.colors?.length > 0 && (
        <OptionRow label={`Colour — ${color}`} options={product.colors} value={color} onChange={setColor} />
      )}
      {product.sizes?.length > 0 && (
        <OptionRow label="Size" options={product.sizes} value={size} onChange={setSize} minWidth="min-w-[42px]" />
      )}

      <div className="mt-auto flex flex-col gap-3 pt-2">
        <button
          data-testid={`quick-view-add-to-cart-${product.slug}`}
          onClick={add}
          disabled={product.stock <= 0}
          className="w-full py-3.5 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-capra-amber transition-colors disabled:opacity-50"
        >
          {product.stock <= 0 ? "Sold Out" : "Add to Cart"}
        </button>
        <Link
          to={`/product/${product.slug}`}
          onClick={onClose}
          className="text-center text-[11px] uppercase tracking-[0.22em] text-capra-muted hover:text-capra-bronze transition-colors"
        >
          View Full Details
        </Link>
      </div>
    </div>
  );
};

export const QuickView = ({ product, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent aria-describedby={undefined} className="max-w-3xl bg-capra-surface border-capra-bronze/25 p-0 gap-0 overflow-hidden" data-testid={`quick-view-dialog-${product.slug}`}>
      <div className="grid sm:grid-cols-2">
        <div className="aspect-[3/4] sm:aspect-auto sm:min-h-[420px]">
          <ProductVisual product={product} emblemSize="medium" />
        </div>
        <QuickViewDetails product={product} onClose={() => onOpenChange(false)} />
      </div>
    </DialogContent>
  </Dialog>
);
